import { z } from 'zod'

import { ProjectTypeEnum } from './project'
import { TaskStatusTypeEnum } from './task-status'

// =====================
// Status Template Types
// =====================

export interface StatusConfig {
  id: string
  name: string
  color: string
  statusTypeId: TaskStatusTypeEnum
  order: number
  wipLimit?: number | null
  isDefault?: boolean
  /**
   * Moving a task into this status requires a comment from the person making
   * the move (SD-145). System actors are exempt. Absent means false.
   */
  requiresComment?: boolean
}

export interface StatusTemplate {
  id: string
  organizationId?: string
  name: string
  description?: string
  isSystem: boolean
  statuses: StatusConfig[]
  metadata: {
    suggestedForTypes?: ProjectTypeEnum[]
    category?: string
    tags?: string[]
  }
  createdAt: Date
  updatedAt: Date
}

// Schema validation
export const StatusConfigSchema = z.object({
  id: z.string(),
  name: z.string(),
  color: z.string().regex(/^#[0-9A-F]{6}$/i),
  statusTypeId: z.nativeEnum(TaskStatusTypeEnum),
  order: z.number(),
  wipLimit: z.number().nullable().optional(),
  isDefault: z.boolean().optional(),
  requiresComment: z.boolean().optional(),
})

export const StatusTemplateSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid().nullable().optional(),
  name: z.string(),
  description: z.string().optional(),
  isSystem: z.boolean(),
  statuses: z.array(StatusConfigSchema),
  metadata: z.object({
    suggestedForTypes: z.array(z.nativeEnum(ProjectTypeEnum)).optional(),
    category: z.string().optional(),
    tags: z.array(z.string()).optional(),
  }),
  createdAt: z.date(),
  updatedAt: z.date(),
})

// =====================
// Project Template Types
// =====================

// Simplified task group template - matches TaskGroup schema
export interface ProjectGroupTemplate {
  id: string
  name: string
  description?: string
  color: string
  order: number
  groupType?: 'phase' | 'swimlane' | 'epic' | 'category'

  // Status configuration - simplified (inherit or custom only)
  statusSource: 'inherit' | 'custom'
  customStatuses?: StatusConfig[]

  // Initial tasks for this group
  initialTasks?: ProjectTaskTemplate[]

  // Settings
  allowParallelTasks?: boolean
  isDefault?: boolean
}

// Simplified task template
export interface ProjectTaskTemplate {
  name: string
  description?: string
  priority?: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT'
  estimatedHours?: number
  dueDate?: string
  scheduledFor?: string
}

// Simplified project template - matches schema exactly
export interface ProjectTemplate {
  id: string
  organizationId?: string
  name: string
  description?: string
  icon?: string
  projectType: ProjectTypeEnum
  isSystem: boolean

  // Status template reference - simple
  statusTemplateId?: string

  // Project groups - simplified (direct array)
  groups: ProjectGroupTemplate[]

  // Project-level initial tasks
  initialTasks?: ProjectTaskTemplate[]

  // Simple metadata
  metadata: {
    category: string
    tags: string[]
  }

  // Usage tracking
  usageCount: number
  lastUsedAt?: Date

  createdAt: Date
  updatedAt: Date
}

// =====================
// DTO Types
// =====================

export interface CreateProjectFromTemplateDto {
  projectName: string
  projectDescription?: string
  entityId?: string
  startDate?: Date | string
  endDate?: Date | string
}

export interface PreviewProjectFromTemplateDto {
  projectName: string
}

export interface ProjectTemplatePreview {
  project: {
    name: string
    description?: string
    projectType: string
    statusTemplateName?: string
  }
  groups: Array<{
    name: string
    description?: string
    color: string
    order: number
    groupType?: string
    statusSource: string
    statuses?: StatusConfig[]
    taskCount: number
  }>
  summary: {
    totalGroups: number
    totalTasks: number
  }
}

// =====================
// Helper Functions
// =====================

export function generateStatusId(): string {
  return `status_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`
}

export function generateTemplateId(): string {
  return `template_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`
}

/**
 * Deterministic UUID generator for system templates
 * Generates consistent UUIDs across all environments based on input string
 * Falls back to static IDs in browser environments where crypto is not available
 */
function generateDeterministicUUID(input: string): string {
  // Check if we're in a Node.js environment (where crypto is available)
  if (
    typeof process !== 'undefined' &&
    process.versions &&
    process.versions.node
  ) {
    try {
      // Dynamic import to avoid bundler issues
      const crypto = require('crypto')
      const hash = crypto.createHash('sha256').update(input).digest('hex')
      // Format as UUID v4-like string
      return [
        hash.substring(0, 8),
        hash.substring(8, 12),
        '4' + hash.substring(13, 16), // Version 4
        '8' + hash.substring(17, 20), // Variant bits
        hash.substring(20, 32),
      ].join('-')
    } catch (e) {
      // Fall through to static IDs if crypto is not available
    }
  }

  // Static fallback IDs for browser environments
  // These match the deterministic IDs generated by the Node.js version
  const staticIds: Record<string, string> = {
    'Simple Workflow': 'b2d0cf19-f90c-42a7-8564-3be24bd7b599',
    'Agile Workflow': 'db32081d-c331-4f99-8f1a-83d081f35fe0',
    'Waterfall Workflow': '09edacba-fa4d-46ce-8210-59f5307af058',
  }

  return (
    staticIds[input] ||
    `fallback-${input.toLowerCase().replace(/\s+/g, '-')}-id`
  )
}

/**
 * System Status Template IDs
 * These IDs are deterministic and consistent across all environments
 */
export const SYSTEM_STATUS_TEMPLATE_IDS = {
  SIMPLE_WORKFLOW: generateDeterministicUUID('Simple Workflow'),
  AGILE_WORKFLOW: generateDeterministicUUID('Agile Workflow'),
  WATERFALL_WORKFLOW: generateDeterministicUUID('Waterfall Workflow'),
} as const

/**
 * Default status template mappings for project types
 * Uses deterministic template IDs for reliable references
 */
export const DEFAULT_PROJECT_TYPE_TEMPLATE_MAPPING = {
  basic: SYSTEM_STATUS_TEMPLATE_IDS.SIMPLE_WORKFLOW,
  scrum: SYSTEM_STATUS_TEMPLATE_IDS.AGILE_WORKFLOW,
  kanban: SYSTEM_STATUS_TEMPLATE_IDS.AGILE_WORKFLOW,
  waterfall: SYSTEM_STATUS_TEMPLATE_IDS.WATERFALL_WORKFLOW,
} as const

/**
 * Default status configurations for each project type
 * Used as fallback when templates are not available
 */
export const DEFAULT_PROJECT_STATUS_CONFIGS = {
  BASIC: [
    {
      id: 'todo',
      name: 'To Do',
      color: '#6B7280',
      statusTypeId: 'NOT_STARTED',
      order: 0,
      isDefault: true,
    },
    {
      id: 'in-progress',
      name: 'In Progress',
      color: '#3B82F6',
      statusTypeId: 'IN_PROGRESS',
      order: 1,
      isDefault: true,
    },
    {
      id: 'done',
      name: 'Done',
      color: '#10B981',
      statusTypeId: 'COMPLETED',
      order: 2,
      isDefault: true,
    },
  ],
  KANBAN: [
    {
      id: 'backlog',
      name: 'Backlog',
      color: '#6B7280',
      statusTypeId: 'NOT_STARTED',
      order: 0,
      isDefault: true,
    },
    {
      id: 'todo',
      name: 'To Do',
      color: '#8B5CF6',
      statusTypeId: 'NOT_STARTED',
      order: 1,
      isDefault: true,
    },
    {
      id: 'in-progress',
      name: 'In Progress',
      color: '#3B82F6',
      statusTypeId: 'IN_PROGRESS',
      order: 2,
      isDefault: true,
    },
    {
      id: 'review',
      name: 'In Review',
      color: '#F59E0B',
      statusTypeId: 'IN_PROGRESS',
      order: 3,
      isDefault: true,
    },
    {
      id: 'done',
      name: 'Done',
      color: '#10B981',
      statusTypeId: 'COMPLETED',
      order: 4,
      isDefault: true,
    },
  ],
  SCRUM: [
    {
      id: 'backlog',
      name: 'Backlog',
      color: '#6B7280',
      statusTypeId: 'NOT_STARTED',
      order: 0,
      isDefault: true,
    },
    {
      id: 'ready',
      name: 'Ready',
      color: '#8B5CF6',
      statusTypeId: 'NOT_STARTED',
      order: 1,
      isDefault: true,
    },
    {
      id: 'in-progress',
      name: 'In Progress',
      color: '#3B82F6',
      statusTypeId: 'IN_PROGRESS',
      order: 2,
      isDefault: true,
    },
    {
      id: 'review',
      name: 'In Review',
      color: '#F59E0B',
      statusTypeId: 'IN_PROGRESS',
      order: 3,
      isDefault: true,
    },
    {
      id: 'done',
      name: 'Done',
      color: '#10B981',
      statusTypeId: 'COMPLETED',
      order: 4,
      isDefault: true,
    },
  ],
  WATERFALL: [
    {
      id: 'planning',
      name: 'Planning',
      color: '#6B7280',
      statusTypeId: 'NOT_STARTED',
      order: 0,
      isDefault: true,
    },
    {
      id: 'development',
      name: 'In Development',
      color: '#3B82F6',
      statusTypeId: 'IN_PROGRESS',
      order: 1,
      isDefault: true,
    },
    {
      id: 'testing',
      name: 'Testing',
      color: '#F59E0B',
      statusTypeId: 'IN_PROGRESS',
      order: 2,
      isDefault: true,
    },
    {
      id: 'completed',
      name: 'Completed',
      color: '#10B981',
      statusTypeId: 'COMPLETED',
      order: 3,
      isDefault: true,
    },
  ],
} as const
